import React from 'react';
import { motion } from 'framer-motion';
export const SanitizationIllustration: React.FC = () => {
  // 4 bottles staggered across the conveyor
  const bottles = [0, 1, 2, 3];
  return <div className="relative w-full h-full bg-mint overflow-hidden" data-id="element-501">
      {/* Tiled lab wall pattern */}
      <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" data-id="element-502">
        <defs data-id="element-503">
          <pattern id="tile-pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-504">
            <rect width="40" height="40" fill="#eaf5ec" data-id="element-505" />
            <rect width="38" height="38" x="1" y="1" fill="none" stroke="#3aa856" strokeWidth="0.3" opacity="0.25" data-id="element-506" />
          </pattern>
        </defs>
        <rect width="100%" height="60%" fill="url(#tile-pattern)" data-id="element-507" />
      </svg>

      {/* Wash chamber housing — top spray bar */}
      <div className="absolute top-6 left-6 right-6 h-8 bg-primary rounded-md shadow-glossy flex items-center px-3" data-id="element-508">
        <div className="flex-1 flex justify-between items-center" data-id="element-509">
          <div className="font-mono text-[9px] text-leaf uppercase tracking-wider" data-id="element-510">
            CIP WASH UNIT
          </div>
          <div className="flex items-center gap-1.5" data-id="element-511">
            <span className="w-1.5 h-1.5 rounded-full bg-leaf animate-pulse" data-id="element-512" />
            <span className="font-mono text-[9px] text-white" data-id="element-513">ACTIVE</span>
          </div>
        </div>
      </div>

      {/* Spray jets — 5 jets pointing down from the wash bar */}
      <div className="absolute top-14 left-6 right-6 h-2 flex justify-around" data-id="element-514">
        {[0, 1, 2, 3, 4].map(i => <div key={i} className="relative w-2 h-2 bg-primary rounded-b-md" data-id="element-515" />)}
      </div>

      {/* Animated water streams */}
      <svg viewBox="0 0 400 264" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice" data-id="element-516">
        {/* Water spray streams */}
        {[40, 100, 160, 220, 280, 340].map((x, i) => <motion.line key={i} x1={x} x2={x} y1="58" y2="58" stroke="#2d8fa8" strokeWidth="1.5" strokeLinecap="round" opacity="0.6" animate={{
        y2: [58, 160, 58],
        opacity: [0.2, 0.8, 0.2]
      }} transition={{
        duration: 0.8,
        repeat: Infinity,
        delay: i * 0.15,
        ease: 'easeIn'
      }} data-id="element-517" />)}

        {/* Falling water droplets */}
        {[55, 115, 175, 235, 295, 355].map((x, i) => <motion.circle key={`drop-${i}`} cx={x} cy="60" r="1.5" fill="#2d8fa8" animate={{
        cy: [60, 170],
        opacity: [0, 1, 0]
      }} transition={{
        duration: 1,
        repeat: Infinity,
        delay: 0.4 + i * 0.2,
        ease: 'easeIn'
      }} data-id="element-518" />)}
      </svg>

      {/* Bubbles floating up */}
      {[{
      x: '18%',
      delay: 0
    }, {
      x: '32%',
      delay: 0.6
    }, {
      x: '50%',
      delay: 1.2
    }, {
      x: '68%',
      delay: 0.3
    }, {
      x: '82%',
      delay: 0.9
    }].map((b, i) => <motion.div key={`bubble-${i}`} className="absolute w-2 h-2 rounded-full bg-white/70 border border-sky/30" style={{
      left: b.x,
      bottom: '70px'
    }} animate={{
      y: [0, -60, -100],
      opacity: [0, 0.9, 0],
      scale: [0.6, 1, 0.8]
    }} transition={{
      duration: 3,
      repeat: Infinity,
      delay: b.delay,
      ease: 'easeOut'
    }} data-id="element-519" />)}

      {/* Conveyor belt */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-primary" data-id="element-520">
        {/* Belt surface with moving stripes */}
        <div className="absolute top-2 left-0 right-0 h-8 bg-ink overflow-hidden" data-id="element-521">
          <motion.div className="flex h-full" animate={{
          x: ['0%', '-20%']
        }} transition={{
          duration: 1.2,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-522">
            {Array.from({
            length: 30
          }).map((_, i) => <div key={i} className="shrink-0 w-6 h-full border-r border-muted/30" data-id="element-523" />)}
          </motion.div>
        </div>
        {/* Belt rollers */}
        <div className="absolute top-1 left-2 w-3 h-3 rounded-full bg-muted" data-id="element-524" />
        <div className="absolute top-1 right-2 w-3 h-3 rounded-full bg-muted" data-id="element-525" />
        <div className="absolute bottom-1 left-2 w-3 h-3 rounded-full bg-muted" data-id="element-526" />
        <div className="absolute bottom-1 right-2 w-3 h-3 rounded-full bg-muted" data-id="element-527" />
      </div>

      {/* Bottles moving on conveyor — left to right, dirty -> clean transition */}
      {bottles.map(i => <motion.div key={`bottle-${i}`} className="absolute" style={{
      bottom: '54px'
    }} initial={{
      left: '-15%'
    }} animate={{
      left: ['-15%', '110%']
    }} transition={{
      duration: 6,
      repeat: Infinity,
      delay: i * 1.5,
      ease: 'linear'
    }} data-id="element-528">
          <BottleOnConveyor data-id="element-529" />
        </motion.div>)}

      {/* 99% Cleanliness Badge */}
      <motion.div className="absolute bottom-24 right-6 bg-white rounded-2xl px-4 py-3 shadow-glossy-lg border border-leaf/30" initial={{
      opacity: 0,
      scale: 0.8
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      delay: 0.5
    }} data-id="element-530">
        <div className="flex items-center gap-3" data-id="element-531">
          {/* Circular progress */}
          <div className="relative w-12 h-12" data-id="element-532">
            <svg viewBox="0 0 40 40" className="w-full h-full -rotate-90" data-id="element-533">
              <circle cx="20" cy="20" r="16" fill="none" stroke="#eaf5ec" strokeWidth="4" data-id="element-534" />
              <motion.circle cx="20" cy="20" r="16" fill="none" stroke="#3aa856" strokeWidth="4" strokeLinecap="round" strokeDasharray="100.5" initial={{
              strokeDashoffset: 100.5
            }} animate={{
              strokeDashoffset: 1
            }} transition={{
              duration: 2,
              delay: 0.7,
              ease: 'easeOut'
            }} data-id="element-535" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-536">
              <span className="font-mono text-[10px] font-medium text-primary" data-id="element-537">
                99%
              </span>
            </div>
          </div>
          <div data-id="element-538">
            <div className="font-mono text-[9px] text-muted uppercase tracking-wider leading-tight" data-id="element-539">
              Cleanliness
            </div>
            <div className="font-display text-sm text-primary leading-tight" data-id="element-540">
              Sanitized
            </div>
          </div>
        </div>
      </motion.div>

      {/* Steam wisps from chamber */}
      <motion.div className="absolute top-12 left-12 w-8 h-12 rounded-full bg-white/30 blur-md" animate={{
      opacity: [0, 0.6, 0],
      y: [0, -20, -30]
    }} transition={{
      duration: 3,
      repeat: Infinity,
      ease: 'easeOut'
    }} data-id="element-541" />
      <motion.div className="absolute top-12 right-16 w-6 h-10 rounded-full bg-white/25 blur-md" animate={{
      opacity: [0, 0.5, 0],
      y: [0, -15, -25]
    }} transition={{
      duration: 3,
      repeat: Infinity,
      delay: 1.2,
      ease: 'easeOut'
    }} data-id="element-542" />
    </div>;
};
const BottleOnConveyor: React.FC = () => <div className="relative w-8 h-16" data-id="element-543">
    <svg viewBox="0 0 40 80" className="w-full h-full" data-id="element-544">
      {/* Cap */}
      <rect x="14" y="2" width="12" height="8" rx="1" fill="#e0a73d" data-id="element-545" />
      {/* Neck */}
      <rect x="15" y="10" width="10" height="12" fill="#3aa856" data-id="element-546" />
      {/* Body */}
      <path d="M 15 22 Q 8 26 8 36 L 8 72 Q 8 76 12 76 L 28 76 Q 32 76 32 72 L 32 36 Q 32 26 25 22 Z" fill="#3aa856" data-id="element-547" />
      {/* Shine */}
      <path d="M 11 30 Q 10 50 11 70" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" fill="none" data-id="element-548" />
      {/* Sparkle indicating clean */}
      <g transform="translate(22, 38)" data-id="element-549">
        <path d="M 0 -2 L 0.5 -0.5 L 2 0 L 0.5 0.5 L 0 2 L -0.5 0.5 L -2 0 L -0.5 -0.5 Z" fill="white" opacity="0.9" data-id="element-550" />
      </g>
    </svg>
  </div>;